import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";
import api, { formatApiError, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";
import PageMeta from "@/components/PageMeta";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export default function Articles() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/articles")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-16 lg:py-24" data-testid="articles-page">
      <PageMeta
        title="Articles & Field Notes"
        description="Practical breathwork insights for high performers, drawn from training rooms, boardrooms, and clinical research."
      />
      <p className="overline text-primary mb-3">Insights</p>
      <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">
        Articles & Field Notes
      </h1>
      <p className="mt-4 sm:mt-5 text-muted-foreground max-w-2xl leading-relaxed">
        Practical breathwork insights for high performers, drawn from training rooms, boardrooms, and clinical research.
      </p>

      <div className="mt-10 sm:mt-12 grid gap-5 sm:gap-6">
        {loading && (
          <Card className="p-10 text-center text-muted-foreground text-sm">Loading…</Card>
        )}
        {!loading && items.length === 0 && (
          <Card className="p-10 text-center text-muted-foreground text-sm" data-testid="articles-empty">
            No articles published yet. Check back soon.
          </Card>
        )}
        {items.map((a) => (
          <Link
            key={a.id}
            to={`/articles/${a.slug}`}
            className="block group"
            data-testid={`article-card-${a.slug}`}
          >
            <Card className="p-0 overflow-hidden hover:border-primary/40 transition-colors">
              <div className="flex flex-col md:flex-row">
                {a.cover_image_url && (
                  <div className="md:w-64 md:shrink-0 aspect-[16/9] md:aspect-auto md:h-auto bg-muted overflow-hidden">
                    <img
                      src={resolveImageUrl(a.cover_image_url)}
                      alt={a.title}
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-5 sm:p-7 lg:p-8 flex-1">
                  <div className="flex flex-col gap-2 sm:gap-3">
                    <div className="text-xs text-muted-foreground">
                      {formatDate(a.published_at || a.created_at)}
                      {a.author ? ` · ${a.author}` : ""}
                    </div>
                    <h2 className="font-display text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight group-hover:text-primary transition-colors">
                      {a.title}
                    </h2>
                    {a.excerpt && (
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{a.excerpt}</p>
                    )}
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-primary mt-1">
                      Read article <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
